import { useCallback, useContext, useMemo, useState } from "react";
import { Task, TaskGroupBy, TaskStatus } from "@common/types/task";
import type {
    BoardColumnDef,
    BoardPlacement,
} from "@design-system/Board/Board.types";
import { TaskClientNamesContext } from "../taskClientNamesContext";
import type { TaskWorkspaceData } from "../useTaskWorkspaceData";
import type { TaskBoardTask } from "../types";
import type { TaskBoardDisplayState } from "./taskBoardCache";
import { getTaskColumnIcon } from "./taskBoardColumnIcons";
import { buildTaskBoardColumns } from "./taskBoardColumns";
import { getTaskBoardAxisFields } from "./taskBoardFields";
import {
    buildTaskBoardLanes,
    createTaskBoardPlacements,
    ENTITY_LANE_BY,
    type TaskBoardLane,
    type TaskBoardLaneBy,
} from "./taskBoardLanes";
import type { TaskBoardAxes } from "./taskBoardMoves";
import {
    getTaskBoardNavigationIds,
    getTasksByColumnId,
    groupTaskSections,
    type TaskBoardSection,
} from "./taskBoardNavigation";
import { pinTaskOrder, type TaskBoardPin } from "./taskBoardPins";
import { getTaskBoardComparator, type TaskComparator } from "./taskBoardSort";
import { useTaskEntityLookups } from "./useTaskEntityLookups";

const NO_PINS: ReadonlyMap<string, TaskBoardPin> = new Map();

/** What the full board and the open task's column panel both show. */
export interface TaskBoardModel {
    /** The board's column grouping, List's (see `getTaskListGroupBy`). */
    groupBy: TaskGroupBy;
    laneBy: TaskBoardLaneBy | null;
    columns: BoardColumnDef[];
    lanes: TaskBoardLane[] | undefined;
    /** What the columns and lanes stand for, which decides what a drop sets. */
    axes: TaskBoardAxes;
    getPlacements: (task: Task) => BoardPlacement[];
    /** The card order: the board sort, with created tasks kept where their draft opened. */
    compareTasks: TaskComparator;
    /** Keeps a task created at a `+` where its draft opened (see `TaskBoardPin`). */
    pinTask: (taskId: string, pin: TaskBoardPin) => void;
    /** The `TaskColumn` ids the columns and lanes already show, left off the cards. */
    hiddenFields: ReadonlySet<string>;
    /** Each column's lanes with their tasks, in board order. */
    sectionsByColumnId: ReadonlyMap<
        string,
        Array<TaskBoardSection<TaskBoardTask>>
    >;
    /** Each column's tasks in reading order, each once. */
    tasksByColumnId: ReadonlyMap<string, TaskBoardTask[]>;
    /** Board reading order, for the detail's pager. */
    navigationTaskIds: string[];
}

interface UseTaskBoardModelArgs {
    tasks: readonly TaskBoardTask[];
    groupBy: TaskGroupBy;
    statusFilter: TaskStatus[] | undefined;
    workspaceData: TaskWorkspaceData;
    display: Pick<
        TaskBoardDisplayState,
        "isEmptyColumnsShown" | "sort" | "sortDirection"
    >;
    /** The swimlanes to draw, already checked against what the page offers. */
    laneBy: TaskBoardLaneBy | null;
    isCrossWorkspace: boolean;
}

/** The board's columns, lanes, card order and pager order for the page's tasks. */
export const useTaskBoardModel = ({
    tasks,
    groupBy,
    statusFilter,
    workspaceData,
    display,
    laneBy,
    isCrossWorkspace,
}: UseTaskBoardModelArgs): TaskBoardModel => {
    const clientNamesById = useContext(TaskClientNamesContext);
    const { entityNamesById, entityWorkspaceIds } = useTaskEntityLookups({
        tasks,
        isCrossWorkspace,
        isEnabled: laneBy === ENTITY_LANE_BY,
        entitiesNameMap: workspaceData.entitiesNameMap,
    });

    const { columns, lanes } = useMemo(() => {
        const axisArgs = {
            tasks,
            statusFilter,
            lookups: { ...workspaceData, clientNamesById },
            isEmptyColumnsShown: display.isEmptyColumnsShown,
            getIcon: getTaskColumnIcon,
        };
        return {
            columns: buildTaskBoardColumns({ ...axisArgs, groupBy }),
            lanes: buildTaskBoardLanes({
                ...axisArgs,
                laneBy,
                entityNamesById,
            }),
        };
    }, [
        tasks,
        groupBy,
        laneBy,
        statusFilter,
        workspaceData,
        clientNamesById,
        entityNamesById,
        display.isEmptyColumnsShown,
    ]);

    const [pins, setPins] = useState(NO_PINS);
    const pinTask = useCallback(
        (taskId: string, pin: TaskBoardPin) =>
            setPins((previous) => new Map(previous).set(taskId, pin)),
        [],
    );
    const { sort, sortDirection } = display;
    const compareTasks = useMemo(
        () =>
            pinTaskOrder({
                tasks,
                pins,
                sort,
                compareTasks: getTaskBoardComparator({
                    sort,
                    direction: sortDirection,
                    clientNamesById,
                }),
            }),
        [tasks, pins, sort, sortDirection, clientNamesById],
    );

    return useMemo(() => {
        const getPlacements = createTaskBoardPlacements({ groupBy, laneBy });
        const sectionsByColumnId = groupTaskSections({
            tasks,
            columns,
            lanes,
            getPlacements,
            compareTasks,
        });
        const tasksByColumnId = getTasksByColumnId(sectionsByColumnId);
        return {
            groupBy,
            laneBy,
            columns,
            lanes,
            axes: { groupBy, laneBy, entityWorkspaceIds, isCrossWorkspace },
            getPlacements,
            compareTasks,
            pinTask,
            hiddenFields: getTaskBoardAxisFields({ groupBy, laneBy }),
            sectionsByColumnId,
            tasksByColumnId,
            navigationTaskIds: getTaskBoardNavigationIds({
                columns,
                tasksByColumnId,
            }),
        };
    }, [
        tasks,
        columns,
        lanes,
        compareTasks,
        pinTask,
        groupBy,
        laneBy,
        entityWorkspaceIds,
        isCrossWorkspace,
    ]);
};
